import { createContext, useContext, ReactNode } from 'react';
import { useSiteInfo } from '../hooks/use-site-info';

/** Default timezone if not configured in CMS */
const DEFAULT_TIMEZONE = 'America/Chicago';

interface TimezoneContextValue {
  /** The configured IANA timezone identifier */
  timezone: string;
  /** Whether the timezone is still loading from the API */
  isLoading: boolean;
}

const TimezoneContext = createContext<TimezoneContextValue>({
  timezone: DEFAULT_TIMEZONE,
  isLoading: true,
});

interface TimezoneProviderProps {
  children: ReactNode;
}

/**
 * Provider component that makes the site's configured timezone available
 * to all child components via the useTimezone hook.
 */
export function TimezoneProvider({ children }: TimezoneProviderProps) {
  const { data: siteInfo, isLoading } = useSiteInfo();

  const timezone = siteInfo?.SiteTimezone ?? DEFAULT_TIMEZONE;

  return (
    <TimezoneContext.Provider value={{ timezone, isLoading }}>
      {children}
    </TimezoneContext.Provider>
  );
}

/**
 * Hook to access the site's configured timezone.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { timezone } = useTimezone();
 *   const now = getNowInTimezone(timezone);
 *   // ...
 * }
 * ```
 */
export function useTimezone(): TimezoneContextValue {
  const context = useContext(TimezoneContext);
  return context;
}

/**
 * Get the default timezone (used when context is not available)
 */
export function getDefaultTimezone(): string {
  return DEFAULT_TIMEZONE;
}
